import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  ActionButton,
  Button,
  Card,
  DatePicker,
  Divider,
  Dropdown,
  TextInput,
} from '@makeably/creativex-design-system';
import {
  emptyState,
  filterProps,
  getLinkDimension,
  versionTypes,
  NULL_LABEL,
  NULL_VALUE,
} from 'components/internal/shared';
import FilterTags from 'components/molecules/FilterTags';
import ItemsFilter from 'components/molecules/ItemsFilter';
import ItemsTable from 'components/molecules/ItemsTable';
import { saveFilterQuery } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import {
  filterItems,
  getValueOptions,
} from 'utilities/itemFilter';
import { track } from 'utilities/mixpanel';
import { internalVersionPath } from 'utilities/routes';
import {
  decamelize,
  titleize,
  timestampToDateTime,
} from 'utilities/string';
import {
  getPage,
  getParams,
  redirectWithParamsObj,
  setParam,
} from 'utilities/url';
import styles from './Versions.module.css';

const versionProps = PropTypes.shape({
  event: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  itemId: PropTypes.number.isRequired,
  timestamp: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userCompany: PropTypes.string,
  userName: PropTypes.string,
});

const propTypes = {
  endDate: PropTypes.string,
  filterSelections: filterProps,
  itemId: PropTypes.string,
  limit: PropTypes.number.isRequired,
  maxDate: PropTypes.string.isRequired,
  minDate: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  type: PropTypes.string,
  versions: PropTypes.arrayOf(versionProps).isRequired,
};

const defaultProps = {
  endDate: undefined,
  filterSelections: {},
  itemId: undefined,
  startDate: undefined,
  type: undefined,
};

const filterDimensions = [
  {
    label: 'Company',
    value: 'userCompany',
  },
  {
    label: 'Event',
    value: 'event',
  },
  {
    label: 'User',
    value: 'userName',
  },
];

const headers = [
  {
    key: 'itemId',
    label: 'ID',
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'event',
    label: 'Event',
  },
  {
    key: 'timeChanged',
    label: 'Time Changed',
  },
  {
    key: 'userName',
    label: 'User',
  },
  {
    key: 'userCompany',
    label: 'Company',
  },
];

const sortDefault = {
  asc: false,
  key: 'timeChanged',
};

function getOption(value) {
  return {
    label: decamelize(value),
    value,
  };
}

function getItems(versions) {
  return versions.map(({
    event,
    id,
    itemId,
    timestamp,
    type,
    userCompany,
    userName,
  }) => ({
    event: {
      label: titleize(event),
      value: event,
    },
    id: { value: id },
    itemId: getLinkDimension(itemId, internalVersionPath(id)),
    timeChanged: {
      label: timestampToDateTime(timestamp),
      value: timestamp,
    },
    type: {
      label: decamelize(type),
      value: type,
    },
    userCompany: {
      label: userCompany ?? NULL_LABEL,
      value: userCompany ?? NULL_VALUE,
    },
    userName: {
      label: userName ?? NULL_LABEL,
      value: userName ?? NULL_VALUE,
    },
  }));
}

function Versions({
  endDate: initialEndDate,
  filterSelections: initialFilterSelections,
  itemId: initialItemId,
  limit,
  maxDate,
  minDate,
  startDate: initialStartDate,
  type,
  versions,
}) {
  const params = getParams(window);
  const atLimit = versions.length === limit;
  const [typeOption, setTypeOption] = useState(
    versionTypes.includes(type) ? getOption(type) : null,
  );
  const [itemId, setItemId] = useState(initialItemId ?? '');
  const [endDate, setEndDate] = useState(initialEndDate ?? '');
  const [startDate, setStartDate] = useState(initialStartDate ?? '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSelections, setFilterSelections] = useState(initialFilterSelections);
  const [sort, setSort] = useState(sortDefault);
  const [page, setPage] = useState(getPage(params));
  const [active, setActive] = useState(false);
  const canSearch = Boolean(typeOption);

  const typeOptions = useMemo(() => versionTypes.map(getOption), [versionTypes]);

  const items = useMemo(() => getItems(versions), [versions]);

  const filterOptions = useMemo(() => (
    getValueOptions(filterDimensions, items)
  ), [filterDimensions, items]);

  const filteredItems = useMemo(() => (
    filterItems(items, filterSelections)
  ), [items, filterSelections]);

  const sortedItems = useMemo(() => (
    filteredItems.sort(getItemSortBy(sort.key, sort.asc)).slice()
  ), [filteredItems, sort]);

  const handleSearch = () => {
    if (canSearch) {
      setActive(true);

      const paramsObj = {
        end_date: endDate,
        item_id: itemId,
        start_date: startDate,
        type: typeOption?.value,
      };
      track('search', paramsObj);
      redirectWithParamsObj(paramsObj, params, window);
    }
  };

  const handleClearOptional = () => {
    setItemId('');
    setEndDate('');
    setStartDate('');
  };

  const handleFilterSelect = async (value) => {
    setPage(1);
    setFilterSelections(value);

    if (Object.keys(value).length > 0) {
      const uuid = await saveFilterQuery(value);
      setParam('filter_uuid', uuid, params, window);
    } else {
      setParam('filter_uuid', '', params, window);
    }
  };

  return (
    <Card className="u-flexColumn u-gap-24">
      <div className="u-flexColumn u-gap-16">
        <div className="u-flexRow u-alignEnd u-gap-16">
          <Dropdown
            label="Type"
            menuProps={{ size: 'medium' }}
            options={typeOptions}
            selected={typeOption}
            size="medium"
            onChange={setTypeOption}
          />
          <TextInput
            disabled={!canSearch}
            label="ID (Optional)"
            placeholder="ID"
            size="medium"
            value={itemId}
            onChange={setItemId}
          />
          <div className={styles.date}>
            <div className="t-caption-1">Date Range (Optional)</div>
            <DatePicker
              endDate={endDate}
              maxDate={maxDate}
              minDate={minDate}
              startDate={startDate}
              onEndDateChange={setEndDate}
              onStartDateChange={setStartDate}
            />
          </div>
          <Button
            disabled={!itemId && !endDate && !startDate}
            label="Clear Optional"
            variant="tertiary"
            onClick={handleClearOptional}
          />
          <ActionButton
            active={active}
            disabled={!canSearch}
            label="Search"
            onClick={handleSearch}
          />
        </div>
      </div>
      { atLimit && (
        <div>{ `Showing first ${limit} results. Please further limit by ID or date range` }</div>
      ) }
      <Divider />
      <div className="u-flexRow u-alignCenter u-gap-8">
        <ItemsFilter
          dimensions={filterDimensions}
          isOpen={filterOpen}
          options={filterOptions}
          selections={filterSelections}
          onClose={() => setFilterOpen(false)}
          onOpen={() => setFilterOpen(true)}
          onSelect={handleFilterSelect}
        />
        <FilterTags
          dimensions={filterDimensions}
          selections={filterSelections}
          onClick={() => setFilterOpen(true)}
          onRemove={handleFilterSelect}
        />
      </div>
      <ItemsTable
        className="u-scrollShadowRight"
        emptyTableContent={emptyState}
        headers={headers}
        items={sortedItems}
        page={page}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
      />
    </Card>
  );
}

Versions.propTypes = propTypes;
Versions.defaultProps = defaultProps;

export default Versions;
